import { HttpErrorResponse } from '@angular/common/http';
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Output,
} from '@angular/core';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import {
	DcBaseComponent,
	combineAll,
	concatenateAll,
} from '@datachain/ui-sdk/common';
import { Map, Set } from 'immutable';
import {
	catchError,
	debounceTime,
	distinctUntilChanged,
	EMPTY,
	filter,
	firstValueFrom,
	map,
	Observable,
	of,
	switchMap,
	take,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { NgCommService } from '../../../ajs-ng-communication.service';
import { FunctionalDocumentationKeys } from '../../../core/documentation/functional-doc-keys';
import { replaceMultipleForwardSlash } from '../../../core/utils/replace-multiple-forward-slash';
import { FilenameLanguageTranslationRequester } from '../../../requesters/export-template/filename-language-translation.requester';
import { GdIcons } from '../../../ui/app-gd.icons';
import { ValidationErrorKeys } from '../../../ui/form/validation-erros-keys';
import { ExportTemplateSelector } from '../../store';
import {
	ExportFileConfigurationEntity,
	FileNameGenStrategy,
} from '../export-file-configuration/export-file-configuration.entity';
import { ExportCharsetEntity } from './export-charset.entity';
import {
	ConnectorMode,
	ExportConnectorEntity,
} from './export-connector.entity';
import {
	ExportFormat,
	ExportTemplateDetailsEntity,
} from './export-template-details.entity';
import {
	ExportTemplateDetailsForm,
	ExportTemplateFormControls,
} from './export-template-details.form';
import { ExportTemplateDetailsHelper } from './export-template-details.helper';

type PredictionProjection = {
	path: string | undefined;
	type: ExportFormat | undefined;
	strategy: FileNameGenStrategy | undefined;
	userDefinedLabel: string | undefined;
	shouldApplyCustomExtension: boolean | undefined;
	customExtension: string | undefined;
};

interface IExportOutputFormatOption {
	label: string;
	value: ExportFormat;
}

@Component({
	selector: 'app-export-template-details',
	templateUrl: './export-template-details.component.html',
	styleUrls: ['./export-template-details.component.scss'],
	inputs: ['exportConfig'],
	providers: [ExportTemplateDetailsForm, ExportTemplateDetailsHelper],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportTemplateDetailsComponent extends DcBaseComponent {
	public GdIcons = GdIcons;
	public FunctionalDocumentationKeys = FunctionalDocumentationKeys;
	public ValidationErrorKeys = ValidationErrorKeys;
	public Appearance: MatFormFieldAppearance = 'outline';
	public ExportTemplateFormControls = ExportTemplateFormControls;
	public outputFormats: Array<IExportOutputFormatOption> = [
		{
			value: ExportFormat.CSV,
			label: 'CSV',
		},
		{
			value: ExportFormat.XML,
			label: 'XML',
		},
		{
			value: ExportFormat.JSON,
			label: 'JSON',
		},
		{
			value: ExportFormat.Parquet,
			label: 'Parquet',
		},
		{
			value: ExportFormat.Excel,
			label: 'Excel',
		},
		{
			value: ExportFormat.TXT_META,
			label: 'Txt (Metadata)',
		},
	];

	@Output()
	public notifyOnError = new EventEmitter<boolean>();

	@Output()
	public hasChangedValue = new EventEmitter<boolean>();

	public vo$: Observable<{
		isLoading: boolean;
		isConfigurable: boolean;
		connectors: Map<number, ExportConnectorEntity>;
		charsets: Set<ExportCharsetEntity>;
	}>;

	public constructor(
		private readonly exportTemplateSelector: ExportTemplateSelector,
		private readonly filenameLanguageTranslationRequester: FilenameLanguageTranslationRequester,
		public readonly exportTemplateForm: ExportTemplateDetailsForm,
		public readonly exportTemplateDetailsHelper: ExportTemplateDetailsHelper,
		public readonly ngCommService: NgCommService
	) {
		super();
		this.cmpId = 'export-template';

		this.toObservable<ExportTemplateDetailsEntity>('exportConfig')
			.pipe(
				takeUntil(this.onDestroy$),
				distinctUntilChanged(),
				tap((config) => {
					this.exportTemplateForm.populate(config);
				})
			)
			.subscribe();

		this.exportTemplateForm.form.valueChanges
			.pipe(
				debounceTime(250), // prevents multiple executions on page startup due to form controls firing sequentially
				withLatestFrom(
					this.toObservable<ExportTemplateDetailsEntity>('exportConfig')
				),
				map(([formValues, knownInputEntity]) => {
					if (knownInputEntity.id === -1) {
						// TODO replace with explicit instance saying it is in creation mode; i.e. all modifications is to be saved
						return true;
					}
					return !knownInputEntity.isSameTemplateForSave(
						this.extractFormData()
					);
				}),
				takeUntil(this.onDestroy$),
				tap((res) => this.hasChangedValue.next(res))
			)
			.subscribe();

		this.exportTemplateForm.form.statusChanges
			.pipe(
				takeUntil(this.onDestroy$),
				tap((status) => {
					this.notifyOnError.emit(status === 'INVALID');
				})
			)
			.subscribe();

		this.exportTemplateForm.form.valueChanges
			.pipe(
				debounceTime(250), // prevents multiple executions on page startup due to form controls firing sequentially
				takeUntil(this.onDestroy$),
				map(() => this.predictionProjection()),
				withLatestFrom(
					this.exportTemplateDetailsHelper.pathValueDisabledAndChanged$,
					this.exportTemplateSelector.getLoading$()
				),
				filter(([, , isLoading]) => !isLoading),
				filter(
					([val, isPathChanged]) =>
						val.path !== undefined || val.type !== undefined || isPathChanged
				),
				distinctUntilChanged(
					(prev, curr) =>
						// Compare by value: any of the projected fields changing is a
						// reason to re-predict the file name.
						JSON.stringify(prev[0]) === JSON.stringify(curr[0]) &&
						prev[1] === curr[1]
				),
				switchMap(() => this.predictFileName())
			)
			.subscribe();

		this.vo$ = combineAll({
			isLoading: this.exportTemplateSelector.getLoading$(),
			isConfigurable: this.exportTemplateForm.isConfigurable$,
			charsets: this.exportTemplateSelector.getCharsets$(),
			connectors: this.exportTemplateSelector.getAvailableConnectors$(),
		});
	}

	public async openPathExplorerModal(): Promise<void> {
		const selectedConnector = await firstValueFrom(
			of(this.exportTemplateForm.connector.value)
		);
		if (selectedConnector === null) {
			return;
		}
		if (
			selectedConnector.type !== ConnectorMode.S3 &&
			selectedConnector.type !== ConnectorMode.SFTP &&
			selectedConnector.type !== ConnectorMode.HDFS
		) {
			return;
		}
		let title = '';
		if (selectedConnector.type === ConnectorMode.S3) {
			title = $localize`:i18n=@@export.path.connector.S3:`;
		} else if (selectedConnector.type === ConnectorMode.SFTP) {
			title = $localize`:i18n=@@export.path.connector.SFTP:`;
		} else if (selectedConnector.type === ConnectorMode.HDFS) {
			title = $localize`:i18n=@@export.path.connector.HDFS:`;
		}
		const path = await this.ngCommService.openPathExplorerModal({
			title,
			connectorId: selectedConnector.id,
			type: selectedConnector.type,
		});
		if (path === undefined) {
			return;
		}
		this.exportTemplateForm.updatePath(path);
	}

	// The fields a prediction depends on, read off the live form. Keep the keys
	// serialized for comparison, so the pipe can tell a real change from noise.
	private predictionProjection(): PredictionProjection {
		// getRawValue, not value: path is disabled for a Local connector, and
		// form.value drops disabled controls. Reading undefined there would never
		// match the '/' the entity carries, so the guard would never hold.
		const formValues = this.exportTemplateForm.form.getRawValue();
		const fileNameConfig: ExportFileConfigurationEntity | undefined =
			formValues[ExportTemplateFormControls.FileConfig];
		return {
			path: formValues[ExportTemplateFormControls.Path],
			type: formValues[ExportTemplateFormControls.ExportType],
			strategy: fileNameConfig?.strategy,
			userDefinedLabel: fileNameConfig?.userDefinedLabel,
			shouldApplyCustomExtension: fileNameConfig?.shouldApplyCustomExtension,
			customExtension: fileNameConfig?.customExtension,
		};
	}

	public extractFormData(): ExportTemplateDetailsEntity {
		return this.exportTemplateForm.extract();
	}

	private predictFileName(): Observable<unknown> {
		if (!this.isFileConnector) {
			return EMPTY;
		}

		let currentConfig: ExportTemplateDetailsEntity;
		try {
			currentConfig = this.extractFormData();
		} catch {
			return EMPTY;
		}

		const fileConfig = currentConfig.fileNameConfig;
		const fileNamePattern =
			fileConfig.strategy === FileNameGenStrategy.UserDefinedFileNamePattern &&
			fileConfig.userDefinedLabel !== undefined
				? fileConfig.userDefinedLabel
				: '%uuid';
		const backendFormat =
			currentConfig.outputType === ExportFormat.TXT_META
				? 'TXT'
				: currentConfig.outputType.toUpperCase();

		return this.exportTemplateSelector.getSelectedDatablock$().pipe(
			take(1),
			switchMap((targetDatablockId) =>
				this.filenameLanguageTranslationRequester.predictOutputFileNameWithExtension(
					[currentConfig.path, fileNamePattern],
					targetDatablockId,
					{
						active: fileConfig.shouldApplyCustomExtension,
						value: fileConfig.customExtension,
						format: backendFormat,
					}
				)
			),
			tap((predicted) => {
				this.exportTemplateForm.updatePredictedFileName(
					replaceMultipleForwardSlash(concatenateAll(predicted, '/'))
				);
			}),
			catchError((error: HttpErrorResponse) => {
				console.error(error);
				return EMPTY;
			})
		);
	}

	public get isFileConnector(): boolean {
		const connector = this.exportTemplateForm.connector.value;
		if (!connector) {
			return true;
		}
		const { type } = connector;
		return (
			type !== ConnectorMode.SQL &&
			type !== ConnectorMode.Neo4j &&
			type !== ConnectorMode.NoSQL
		);
	}
}
